import { formatFactorOrDash, formatNumber, formatQuantityOrDash } from '@/lib/format/number';
import { formatDate, formatDateInput, formatDateOrDash } from '@/lib/format/date';
import { formatTextOrDash } from '@/lib/format/text';
import { formValue } from '@/lib/mappers/formValues';
import { countryCodeLabel } from '@/lib/utils/countries';
import {
  CERTIFICATION_STATUS,
  certificateTypeLabel,
  certificationStatusLabel,
  materialDeclarationSettingsLabel,
  materialRoleLabel,
  materialTypeLabel,
  mbPeriodStatusLabel,
  siteTypeLabel,
  MULTIPLE_SITE_TYPES,
} from '@/config/statuses';
import {
  ASSIGN_MATERIALS_FIELDS,
  EMPTY_MB_PERIOD_FORM,
  EMPTY_SITE_FORM,
  ENERGY_PER_KG_UNIT,
  FIRST_TERMINAL_KEY,
  MB_PERIOD_DETAIL_FIELDS,
  MB_PERIOD_FIELDS,
  SITE_FIELDS,
  TERMINAL_FIELDS,
  emptyTerminalValues,
  mbPeriodFieldName,
  terminalFieldName,
} from '../constants/sites.constants';

const countOrDash = (count) => (count > 0 ? formatNumber(count) : '-');

const readSiteTypes = (dto) => {
  const types = dto.site_types?.length ? dto.site_types : (dto.site_type ?? []);
  return [...new Set(types.filter((type) => type && type !== MULTIPLE_SITE_TYPES))];
};

export const processSite = (dto) => ({
  id: dto.id,
  siteName: formatTextOrDash(dto.name),
  siteId: formatTextOrDash(dto.site_id),
  certificates: dto.site_certificate ?? [],
  siteTypes: formatTextOrDash(
    readSiteTypes(dto)
      .map((type) => siteTypeLabel(type))
      .filter(Boolean)
      .join(', ') || null,
  ),
  terminalCount: countOrDash(dto.associated_terminals_count),
  materialCount: countOrDash(dto.associated_material_count),
  processCount: countOrDash(dto.associated_process_count),
  isDeletable: Boolean(dto.is_deletable),
});

export const siteCertificateLabels = (certificates = []) =>
  certificates.map((code) => ({ code, label: certificateTypeLabel(code) })).filter((c) => c.label);

const readCertificateSiteTypes = (certificate) => {
  if (certificate?.site_types?.length) return certificate.site_types;
  const legacy = certificate?.site_type;
  return legacy && legacy !== MULTIPLE_SITE_TYPES ? [legacy] : [];
};

const toCertificateForm = (certificate) => ({
  [SITE_FIELDS.certificateType]: formValue(certificate?.certificate_type),
  [SITE_FIELDS.siteTypes]: readCertificateSiteTypes(certificate),
  [SITE_FIELDS.certificationStatus]: formValue(certificate?.certification_status),
  [SITE_FIELDS.isccCertificateNumber]: formValue(certificate?.certificate_number),
  [SITE_FIELDS.validFrom]: formatDateInput(certificate?.valid_from),
  [SITE_FIELDS.validTo]: formatDateInput(certificate?.valid_to),
});

const toTerminalForm = (terminal, index) => {
  const terminalKey = String(index + 1);
  return {
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.id)]: formValue(terminal.id),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.terminalName)]: formValue(
      terminal.terminal_name,
    ),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.terminalId)]: formValue(terminal.terminal_id),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.addressLine1)]: formValue(
      terminal.address_line_1,
    ),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.addressLine2)]: formValue(
      terminal.address_line_2,
    ),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.city)]: formValue(terminal.city),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.country)]: formValue(terminal.country),
    [terminalFieldName(terminalKey, TERMINAL_FIELDS.zipCode)]: formValue(terminal.zip_code),
  };
};

const toTerminalsForm = (terminals = []) => {
  if (!terminals.length) return null;
  return {
    [SITE_FIELDS.terminalKeys]: terminals.map((terminal, index) => String(index + 1)),
    ...Object.assign({}, ...terminals.map(toTerminalForm)),
  };
};

export const processSiteForm = (dto) => {
  if (!dto) return null;

  return {
    ...EMPTY_SITE_FORM,
    [SITE_FIELDS.siteName]: formValue(dto.site_name),
    [SITE_FIELDS.siteDeclarationName]: formValue(dto.site_declaration_name),
    [SITE_FIELDS.siteId]: formValue(dto.site_id),
    [SITE_FIELDS.addressLine1]: formValue(dto.address_line_1),
    [SITE_FIELDS.addressLine2]: formValue(dto.address_line_2),
    [SITE_FIELDS.city]: formValue(dto.city),
    [SITE_FIELDS.country]: formValue(dto.country),
    [SITE_FIELDS.zipCode]: formValue(dto.zip_code),
    ...toCertificateForm(dto.site_certificate?.[0]),
    ...(toTerminalsForm(dto.terminals) ?? {}),
  };
};

const isTerminalDeletable = (counts) =>
  Boolean(counts) && Object.values(counts).every((count) => !count);

const terminalFormValues = (dto) => ({
  [TERMINAL_FIELDS.terminalName]: formValue(dto.terminal_name),
  [TERMINAL_FIELDS.terminalId]: formValue(dto.terminal_id),
  [TERMINAL_FIELDS.addressLine1]: formValue(dto.address_line_1),
  [TERMINAL_FIELDS.addressLine2]: formValue(dto.address_line_2),
  [TERMINAL_FIELDS.city]: formValue(dto.city),
  [TERMINAL_FIELDS.country]: formValue(dto.country),
  [TERMINAL_FIELDS.zipCode]: formValue(dto.zip_code),
});

const processSiteTerminal = (dto) => ({
  id: dto.id,
  terminalName: formatTextOrDash(dto.terminal_name),
  terminalId: formatTextOrDash(dto.terminal_id),
  materialCount: dto._count?.material_terminal_connection ?? null,
  addressLine1: formatTextOrDash(dto.address_line_1),
  addressLine2: formatTextOrDash(dto.address_line_2),
  city: formatTextOrDash(dto.city),
  country: formatTextOrDash(countryCodeLabel(dto.country)),
  zipCode: formatTextOrDash(dto.zip_code),
  isDeletable: isTerminalDeletable(dto._count),
  formValues: terminalFormValues(dto),
});

export const processAccountTerminals = (rows = []) =>
  rows.map((dto) => ({
    id: String(dto.id),
    siteId: dto.site_id,
    label: dto.display_label ?? formatTextOrDash(dto.terminal_name),
  }));

export const processSiteDetail = (dto) => {
  if (!dto) return null;

  const certificate = dto.site_certificate?.[0];

  return {
    id: dto.id,
    siteName: formatTextOrDash(dto.site_name),
    siteId: formatTextOrDash(dto.site_id),
    siteDeclarationName: formatTextOrDash(dto.site_declaration_name),
    addressLine1: formatTextOrDash(dto.address_line_1),
    addressLine2: formatTextOrDash(dto.address_line_2),
    city: formatTextOrDash(dto.city),
    country: formatTextOrDash(countryCodeLabel(dto.country)),
    zipCode: formatTextOrDash(dto.zip_code),
    lastUpdated: formatDateOrDash(dto.updated_at),
    certificateType: certificate?.certificate_type ?? null,
    certificateTypeLabel: formatTextOrDash(certificateTypeLabel(certificate?.certificate_type)),
    certificationStatus: formatTextOrDash(
      certificationStatusLabel(certificate?.certification_status),
    ),
    isccCertificateNumber: formatTextOrDash(certificate?.certificate_number),
    validFrom: formatDateOrDash(certificate?.valid_from),
    validTo: formatDateOrDash(certificate?.valid_to),
    terminals: (dto.terminals ?? []).map(processSiteTerminal),
  };
};
const processLabel = (process) => {
  if (!process?.processName) return null;
  return process.processId ? `${process.processName} (${process.processId})` : process.processName;
};

const processSiteMaterial = (dto) => ({
  id: dto.id,
  materialName: formatTextOrDash(dto.name),
  materialCode: formatTextOrDash(dto.material_id),
  referenceId: formatTextOrDash(dto.reference_id),
  materialType: formatTextOrDash(materialTypeLabel(dto.material_type)),
  certificateType: dto.certificate_type ?? null,
  certificateTypeLabel: formatTextOrDash(certificateTypeLabel(dto.certificate_type)),
  materialRole: materialRoleLabel(dto.process_material_type),
  process: formatTextOrDash(
    processLabel({ processName: dto.process?.process_name, processId: dto.process?.process_id }),
  ),
  declarationSettings: dto.declaration_status ?? null,
  declarationSettingsLabel: materialDeclarationSettingsLabel(dto.declaration_status),
  declarationUnit: formatTextOrDash(dto.display_unit),
  workingUnit: formatTextOrDash(dto.weight_unit),
  conversionFactor: formatFactorOrDash(dto.weight_quantity),
  energyContent: formatQuantityOrDash(dto.total_energy_per_kg ?? null, ENERGY_PER_KG_UNIT),
  terminalIds: (dto.assigned_terminals ?? []).map((terminal) => terminal.id),
  isInUse: Boolean(dto.materialIsInUse),
});

export const processSiteMaterials = (rows = []) => rows.map(processSiteMaterial);

const periodRange = (from, to) => {
  const start = formatDate(from);
  const end = formatDate(to);
  return start && end ? `${start} - ${end}` : '-';
};

const processMbPeriodDetail = (timePeriod, certificateValidFrom, certificateValidTo) => ({
  id: timePeriod.mbPeriodId,
  periodName: formatTextOrDash(timePeriod.name),
  timePeriod: periodRange(certificateValidFrom, certificateValidTo),
  status: timePeriod.status ?? null,
  statusLabel: mbPeriodStatusLabel(timePeriod.status),
  startDate: formatDateOrDash(timePeriod.startDate),
  endDate: formatDateOrDash(timePeriod.endDate),
});

const toMbPeriodDetailForm = (timePeriod) => {
  const periodKey = String(timePeriod.id);
  const read = (field) => mbPeriodFieldName(periodKey, field);

  return {
    [read(MB_PERIOD_DETAIL_FIELDS.name)]: formValue(timePeriod.name),
    [read(MB_PERIOD_DETAIL_FIELDS.startDate)]: formatDateInput(timePeriod.startDate),
    [read(MB_PERIOD_DETAIL_FIELDS.endDate)]: formatDateInput(timePeriod.endDate),
    [read(MB_PERIOD_DETAIL_FIELDS.status)]: formValue(timePeriod.status),
  };
};

const toMbPeriodForm = (dto, timePeriods) => ({
  ...EMPTY_MB_PERIOD_FORM,
  [MB_PERIOD_FIELDS.terminalIds]: dto.terminalId == null ? [] : [String(dto.terminalId)],
  [MB_PERIOD_FIELDS.numberOfPeriods]: String(timePeriods.length),
  [MB_PERIOD_FIELDS.periodKeys]: timePeriods.map((timePeriod) => String(timePeriod.id)),
  ...Object.assign({}, ...timePeriods.map(toMbPeriodDetailForm)),
});

export const processAccountMbPeriods = (dto) => ({
  isEmpty: dto?.isEmpty === true,
});

export const processSiteMbPeriod = (dto) => {
  if (!dto) return null;

  const timePeriods = dto.timePeriods ?? [];

  return {
    id: dto.id,
    terminalId: dto.terminalId ?? null,
    rows: timePeriods.map((timePeriod) =>
      processMbPeriodDetail(timePeriod, dto.certificateValidFrom, dto.certificateValidTo),
    ),
    formValues: toMbPeriodForm(dto, timePeriods),
  };
};

export const buildMbPeriodDto = (values) => {
  const periodKeys = values[MB_PERIOD_FIELDS.periodKeys] ?? [];
  const read = (periodKey, field) => values[mbPeriodFieldName(periodKey, field)];

  const timePeriods = periodKeys.map((periodKey) => ({
    id: Number(periodKey),
    name: trimmed(read(periodKey, MB_PERIOD_DETAIL_FIELDS.name)),
    startDate: read(periodKey, MB_PERIOD_DETAIL_FIELDS.startDate),
    endDate: read(periodKey, MB_PERIOD_DETAIL_FIELDS.endDate),
  }));

  return {
    certificateValidFrom: timePeriods[0]?.startDate,
    certificateValidTo: timePeriods[timePeriods.length - 1]?.endDate,
    numberOfPeriods: timePeriods.length,
    timePeriods,
    terminalIds: (values[MB_PERIOD_FIELDS.terminalIds] ?? []).map(Number),
  };
};

export const buildAssignMaterialsDto = (values) => ({
  materials: (values[ASSIGN_MATERIALS_FIELDS.materialIds] ?? []).map((materialId) => ({
    materialId: Number(materialId),
    terminalIds: [Number(values[ASSIGN_MATERIALS_FIELDS.terminalId])],
  })),
});

const trimmed = (value) => (value ?? '').trim();

const buildSiteCertificate = (values) => {
  const isCertified = values[SITE_FIELDS.certificationStatus] === CERTIFICATION_STATUS.certified;
  const siteTypes = values[SITE_FIELDS.siteTypes] ?? [];

  return {
    certificateType: values[SITE_FIELDS.certificateType],
    siteTypes,
    siteType: siteTypes[0],
    certificationStatus: values[SITE_FIELDS.certificationStatus],
    isccCertificateNumber: isCertified ? trimmed(values[SITE_FIELDS.isccCertificateNumber]) : null,
    isISCC_verified: isCertified,
    validFrom: isCertified ? values[SITE_FIELDS.validFrom] : null,
    validTo: isCertified ? values[SITE_FIELDS.validTo] : null,
  };
};

const buildTerminal = (values, terminalKey) => {
  const read = (field) => trimmed(values[terminalFieldName(terminalKey, field)]);

  return {
    terminalName: read(TERMINAL_FIELDS.terminalName),
    terminalId: read(TERMINAL_FIELDS.terminalId),
    addressLine1: read(TERMINAL_FIELDS.addressLine1),
    addressLine2: read(TERMINAL_FIELDS.addressLine2) || null,
    city: read(TERMINAL_FIELDS.city),
    country: read(TERMINAL_FIELDS.country),
    zipCode: read(TERMINAL_FIELDS.zipCode),
  };
};

const buildTerminals = (values) =>
  (values[SITE_FIELDS.terminalKeys] ?? []).map((terminalKey) => {
    const savedId = trimmed(values[terminalFieldName(terminalKey, TERMINAL_FIELDS.id)]);
    return {
      ...(savedId ? { id: savedId } : {}),
      ...buildTerminal(values, terminalKey),
    };
  });

export const buildTerminalDto = (values, terminalKey, siteId) => ({
  ...buildTerminal(values, terminalKey),
  siteId,
});

export const toTerminalModalValues = (terminal) => {
  const base = {
    [SITE_FIELDS.terminalKeys]: [FIRST_TERMINAL_KEY],
    ...emptyTerminalValues(FIRST_TERMINAL_KEY),
  };
  if (!terminal) return base;

  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(terminal.formValues).map(([field, value]) => [
        terminalFieldName(FIRST_TERMINAL_KEY, field),
        value,
      ]),
    ),
  };
};

export const buildSiteDto = (values) => {
  const terminals = buildTerminals(values);

  return {
    siteName: trimmed(values[SITE_FIELDS.siteName]),
    siteDeclarationName: trimmed(values[SITE_FIELDS.siteDeclarationName]),
    siteId: trimmed(values[SITE_FIELDS.siteId]),
    addressLine1: trimmed(values[SITE_FIELDS.addressLine1]),
    addressLine2: trimmed(values[SITE_FIELDS.addressLine2]) || null,
    city: trimmed(values[SITE_FIELDS.city]),
    country: trimmed(values[SITE_FIELDS.country]),
    zipCode: trimmed(values[SITE_FIELDS.zipCode]),
    certificates: [buildSiteCertificate(values)],
    ...(terminals.length ? { terminals } : {}),
  };
};
